import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Star } from "lucide-react";
import { plans } from "../mock/mock";
import { Button } from "./ui/button";
import { fetchPlansConfig } from "../lib/api";

const computeStatus = (o) => (o && typeof o.status === "string" ? o.status : "Available");
const computePrice = (base, o) =>
  o && typeof o.price === "number" && !isNaN(o.price) ? o.price : base;

const StatusPill = ({ text }) => {
  if (!text) return null;
  const negative = text.toLowerCase().includes("not");
  const color = negative
    ? "bg-slate-100 text-slate-500 border-slate-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border ${color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${negative ? "bg-slate-400" : "bg-emerald-500"}`} />
      {text}
    </span>
  );
};

const MembershipPreview = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({});

  const load = async () => {
    try { setCfg(await fetchPlansConfig()); } catch {}
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("ep-data-updated", h);
    const iv = setInterval(load, 20000);
    return () => { window.removeEventListener("ep-data-updated", h); clearInterval(iv); };
  }, []);

  return (
    <section className="bg-ep-royal py-16 lg:py-24 relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[#ff5a5a] opacity-10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-14">
          <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase">
            Membership
          </div>
          <h2 className="text-white text-3xl sm:text-4xl font-bold mt-3">
            Choose a plan that fits your bankroll
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">
            All plans include research delivered to your inbox before kick-off,
            with full access to the public results archive.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {plans.map((p) => {
            const o = cfg[p.id];
            const price = computePrice(p.price, o);
            const status = computeStatus(o);
            return (
            <div
              key={p.id}
              className={`relative rounded-2xl p-8 card-lift ${
                p.highlighted
                  ? "bg-white ring-2 ring-[#ff5a5a] shadow-2xl"
                  : "bg-white"
              }`}
            >
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ff5a5a] text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                  <Star size={12} className="fill-white" />
                  {p.badge}
                </div>
              )}
              <h3 className="text-xl font-bold text-[#0a1a4a]">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-[#0a1a4a]">
                  {p.currency}{price}
                </span>
                <span className="text-[#0a1a4a]/60 text-sm">/ {p.period}</span>
              </div>
              {status && (
                <div className="mt-3">
                  <StatusPill text={status} />
                </div>
              )}

              <ul className="mt-6 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#0a1a4a]/80">
                    <div className="w-5 h-5 rounded-full bg-[#ff5a5a]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={12} className="text-[#ff5a5a]" strokeWidth={3} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Button
            onClick={() => navigate("/membership")}
            variant="outline"
            className="bg-transparent border-white/30 text-white hover:bg-white/10 h-12 px-8 rounded-lg font-semibold"
          >
            View all plan details
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MembershipPreview;
